// SetupStepperModal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import useClassesStore from '../../stateManagers/store/useClassesStore';
import { useSubjectsByClass } from '../../stateManagers/hooks/useSubjectsByClass';
import useTimetableTypes from '../../stateManagers/hooks/useTimetableTypes';
import { useTimeSlots } from '../../stateManagers/hooks/useTimeSlots';

type StepStatus = 'completed' | 'current' | 'checking' | 'pending';

interface SetupStepperModalProps {
  open: boolean;
  onClose: () => void;
  // Adjust these to wherever each screen actually lives in your app.
  classesRoute?: string;
  subjectsRoute?: string;
  timetableRoute?: string;
  examTypesRoute?: string;
  feeTypesRoute?: string;
  feeStructuresRoute?: string;
  feeLateConfigRoute?: string;
}

const STATUS_META: Record<StepStatus, { circle: string; pillBg: string; pillColor: string; label: string }> = {
  completed: { circle: '#10b981', pillBg: '#dcfce7', pillColor: '#15803d', label: 'Completed' },
  current:   { circle: '#3b82f6', pillBg: '#dbeafe', pillColor: '#1d4ed8', label: 'Action Needed' },
  checking:  { circle: '#3b82f6', pillBg: '#dbeafe', pillColor: '#1d4ed8', label: 'Checking...' },
  pending:   { circle: '#e5e7eb', pillBg: '#f3f4f6', pillColor: '#9ca3af', label: 'Pending' },
};

const slotTypeColors: Record<string, { bg: string; color: string }> = {
  class:      { bg: '#eff6ff', color: '#1d4ed8' },
  break:      { bg: '#fef2f2', color: '#b91c1c' },
  lab:        { bg: '#f0fdf4', color: '#15803d' },
  assembly:   { bg: '#fdf4ff', color: '#a21caf' },
  power_nap:  { bg: '#fff7ed', color: '#c2410c' },
  workbook:   { bg: '#fefce8', color: '#a16207' },
  activities: { bg: '#ecfeff', color: '#0e7490' },
};

const examStatusColors: Record<string, { bg: string; color: string }> = {
  active:   { bg: '#dcfce7', color: '#15803d' },
  inactive: { bg: '#f3f4f6', color: '#6b7280' },
};

const lateFeeTypeColors: Record<string, { bg: string; color: string }> = {
  per_day:    { bg: '#eff6ff', color: '#1d4ed8' },
  per_month:  { bg: '#f0fdf4', color: '#15803d' },
  percentage: { bg: '#fdf4ff', color: '#a21caf' },
  none:       { bg: '#f3f4f6', color: '#6b7280' },
};

// Formats a raw amount (number or numeric string) as a consistent currency string.
const formatCurrency = (value: any): string => {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
};

// "2025-04" -> "Apr 2025". Falls back to the raw value if it doesn't parse.
const formatMonthYear = (monthYear: string): string => {
  if (!monthYear) return '—';
  const [y, m] = monthYear.split('-');
  const idx = Number(m) - 1;
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (!y || idx < 0 || idx > 11 || Number.isNaN(idx)) return monthYear;
  return `${names[idx]} ${y}`;
};

const StepCircle: React.FC<{ status: StepStatus; onClick: () => void; selected: boolean }> = ({ status, onClick, selected }) => {
  const meta = STATUS_META[status];
  const isActive = status === 'current' || status === 'checking';
  const glow = isActive
    ? '0 0 0 6px rgba(59,130,246,0.15), 0 0 18px 6px rgba(59,130,246,0.35)'
    : status === 'completed'
    ? '0 0 0 5px rgba(16,185,129,0.14)'
    : 'none';
  const selectedRing = selected ? '0 0 0 3px #1e3a8a' : '';
  const boxShadow = [selectedRing, glow].filter(Boolean).join(', ') || 'none';

  return (
    <button
      onClick={onClick}
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: meta.circle,
        border: '2.5px solid #ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow,
        cursor: 'pointer',
        padding: 0,
        transition: 'background 0.25s, box-shadow 0.3s, border-color 0.2s',
      }}
    >
      {status === 'completed' && <i className="pi pi-check" style={{ color: 'white', fontSize: 15 }} />}
      {status === 'current' && <i className="pi pi-exclamation-triangle" style={{ color: 'white', fontSize: 13 }} />}
      {status === 'checking' && <i className="pi pi-spin pi-spinner" style={{ color: 'white', fontSize: 13.5 }} />}
      {status === 'pending' && <i className="pi pi-ellipsis-h" style={{ color: '#9ca3af', fontSize: 12 }} />}
    </button>
  );
};

const SetupStepperModal: React.FC<SetupStepperModalProps> = ({
  open,
  onClose,
  classesRoute = '/settings',
  subjectsRoute = '/subjects',
  timetableRoute = '/timetable',
  examTypesRoute = '/exam-types',
  feeTypesRoute = '/fee-types',
  feeStructuresRoute = '/fee-structures',
  feeLateConfigRoute = '/fee-late-config',
}) => {
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);

  // ── Step 1: Classes ──────────────────────────────────────────────
  const [classesRefreshTick, setClassesRefreshTick] = useState(0);
  const { classOptions, fetchClasses, hasFetched, isFetching, reset: resetClasses } = useClassesStore();

  useEffect(() => {
    if (open) fetchClasses(false);
  }, [open, fetchClasses, classesRefreshTick]);

  const realClassOptions = classOptions.filter((c) => c.id !== '');
  const classesChecking = isFetching || !hasFetched;
  const classesComplete = hasFetched && realClassOptions.length > 0;
  const step1Status: StepStatus = classesChecking ? 'checking' : classesComplete ? 'completed' : 'current';

  const refreshClasses = () => {
    resetClasses();
    setClassesRefreshTick((t) => t + 1);
  };

  // ── Step 2: Subjects — checked per class ────────────────────────
  const [selectedSubjectClassId, setSelectedSubjectClassId] = useState<string | null>(null);
  const [subjectsRefreshTick, setSubjectsRefreshTick] = useState(0);
  const [subjectsScanLoading, setSubjectsScanLoading] = useState(false);
  const [subjectsScanDone, setSubjectsScanDone] = useState(false);
  const [subjectCounts, setSubjectCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!selectedSubjectClassId && realClassOptions.length > 0) {
      setSelectedSubjectClassId(realClassOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realClassOptions.length]);

  // Scans every class's subject list once classes have finished loading — this is
  // what decides completion, independent of whichever class is being browsed
  // in the drill-down table below. Runs whether classes turned out empty or not,
  // so this step is never blocked on Classes being formally "completed".
  useEffect(() => {
    if (!open || classesChecking) return;
    let cancelled = false;
    setSubjectsScanLoading(true);
    setSubjectsScanDone(false);

    Promise.all(
      realClassOptions.map((c) =>
        apiClient
          .get(`/subjects_by_class?class_id=${c.id}`)
          .then((res) => ({ classId: c.id, count: res.data?.status === 'success' ? (res.data.subjects?.length || 0) : 0 }))
          .catch(() => ({ classId: c.id, count: 0 }))
      )
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, number> = {};
      results.forEach((r) => { map[r.classId] = r.count; });
      setSubjectCounts(map);
      setSubjectsScanLoading(false);
      setSubjectsScanDone(true);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classesChecking, realClassOptions.length, subjectsRefreshTick]);

  const classesMissingSubjects = realClassOptions.filter((c) => (subjectCounts[c.id] ?? 0) === 0);
  const subjectsChecking = !classesChecking && (subjectsScanLoading || !subjectsScanDone);
  const subjectsComplete =
    !classesChecking && subjectsScanDone && realClassOptions.length > 0 && classesMissingSubjects.length === 0;

  const step2Status: StepStatus = useMemo(() => {
    if (classesChecking) return 'pending';
    if (subjectsChecking) return 'checking';
    return subjectsComplete ? 'completed' : 'current';
  }, [classesChecking, subjectsChecking, subjectsComplete]);

  // Drill-down table for whichever class is selected via the chips below.
  const { subjectOptions: viewedSubjects, loading: viewedSubjectsLoading } = useSubjectsByClass(selectedSubjectClassId, false);

  // ── Step 3: Timetable types + time slots ────────────────────────
  // Fetched unconditionally — this step's own data is what decides its status,
  // not whether Classes/Subjects are formally "completed".
  const timetableTypeOptions = useTimetableTypes(false);
  const realTypeOptions = timetableTypeOptions.filter((t) => t.id !== '');
  const typesPresent = realTypeOptions.length > 0;
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedTypeId && realTypeOptions.length > 0) {
      setSelectedTypeId(realTypeOptions[0].id);
    }
  }, [realTypeOptions, selectedTypeId]);

  const firstTypeId = realTypeOptions[0]?.id ?? null;
  const { timeSlots: firstTypeSlots, loading: firstSlotsLoading } = useTimeSlots(firstTypeId);
  const { timeSlots: viewedSlots, loading: viewedSlotsLoading } = useTimeSlots(selectedTypeId);

  const timetableChecking = typesPresent && firstSlotsLoading;
  const timetableComplete = typesPresent && firstTypeSlots.length > 0;

  const step3Status: StepStatus = useMemo(() => {
    if (timetableChecking) return 'checking';
    return timetableComplete ? 'completed' : 'current';
  }, [timetableChecking, timetableComplete]);

  // ── Step 4: Exam Types — fetched directly (not via useExamTypes,        ──
  // which only exposes { label, value, id } and drops name/exam_order/status).
  // Fetched as soon as the modal opens, independent of Steps 1-3's status.
  // API response shape: { status: 'success', data: [{ id, name, title, exam_order, status, ... }] }
  const [examTypes, setExamTypes] = useState<any[]>([]);
  const [examTypesLoading, setExamTypesLoading] = useState(true);
  const [examTypesError, setExamTypesError] = useState<string | null>(null);
  const [examTypesFetched, setExamTypesFetched] = useState(false);
  const [examTypesRefreshTick, setExamTypesRefreshTick] = useState(0);
  const [selectedExamTypeId, setSelectedExamTypeId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setExamTypesLoading(true);
    setExamTypesError(null);

    apiClient
      .get('exam-types')
      .then((res: any) => {
        if (cancelled) return;
        if (res.data?.status === 'success') {
          const list = res.data.data || [];
          setExamTypes(list);
          setSelectedExamTypeId((prev) => prev ?? list[0]?.id ?? null);
        } else {
          setExamTypes([]);
          setExamTypesError(res.data?.message || 'Failed to load exam types.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setExamTypes([]);
        setExamTypesError(err?.response?.data?.message || err?.message || 'An error occurred while fetching exam types.');
      })
      .finally(() => {
        if (cancelled) return;
        setExamTypesLoading(false);
        setExamTypesFetched(true);
      });

    return () => { cancelled = true; };
  }, [open, examTypesRefreshTick]);

  const examTypesChecking = examTypesLoading || !examTypesFetched;
  const examTypesComplete = examTypesFetched && !examTypesError && examTypes.length > 0;

  const step4Status: StepStatus = useMemo(() => {
    if (examTypesChecking) return 'checking';
    return examTypesComplete ? 'completed' : 'current';
  }, [examTypesChecking, examTypesComplete]);

  const sortedExamTypes = useMemo(
    () => [...examTypes].sort((a: any, b: any) => (a.exam_order ?? 0) - (b.exam_order ?? 0)),
    [examTypes]
  );

  // ── Step 5: Fee Types ────────────────────────────────────────────
  // GET /fee-types → { status, message, data: FeeType[], count }
  const [feeTypes, setFeeTypes] = useState<any[]>([]);
  const [feeTypesLoading, setFeeTypesLoading] = useState(true);
  const [feeTypesError, setFeeTypesError] = useState<string | null>(null);
  const [feeTypesFetched, setFeeTypesFetched] = useState(false);
  const [feeTypesRefreshTick, setFeeTypesRefreshTick] = useState(0);
  const [selectedFeeTypeId, setSelectedFeeTypeId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFeeTypesLoading(true);
    setFeeTypesError(null);

    apiClient
      .get('api/fee-types')
      .then((res: any) => {
        if (cancelled) return;
        if (res.data?.status === 'success') {
          const list = res.data.data || [];
          setFeeTypes(list);
          setSelectedFeeTypeId((prev) => prev ?? list[0]?.id ?? null);
        } else {
          setFeeTypes([]);
          setFeeTypesError(res.data?.message || 'Failed to load fee types.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setFeeTypes([]);
        setFeeTypesError(err?.response?.data?.message || err?.message || 'An error occurred while fetching fee types.');
      })
      .finally(() => {
        if (cancelled) return;
        setFeeTypesLoading(false);
        setFeeTypesFetched(true);
      });

    return () => { cancelled = true; };
  }, [open, feeTypesRefreshTick]);

  const feeTypesChecking = feeTypesLoading || !feeTypesFetched;
  const feeTypesComplete = feeTypesFetched && !feeTypesError && feeTypes.length > 0;

  const step5Status: StepStatus = useMemo(() => {
    if (feeTypesChecking) return 'checking';
    return feeTypesComplete ? 'completed' : 'current';
  }, [feeTypesChecking, feeTypesComplete]);

  // ── Step 6: Fee Structures ───────────────────────────────────────
  // GET /fee-structures → { status, message, data: (FeeStructure + fee_name + class_name)[], pagination }
  const [feeStructures, setFeeStructures] = useState<any[]>([]);
  const [feeStructuresLoading, setFeeStructuresLoading] = useState(true);
  const [feeStructuresError, setFeeStructuresError] = useState<string | null>(null);
  const [feeStructuresFetched, setFeeStructuresFetched] = useState(false);
  const [feeStructuresRefreshTick, setFeeStructuresRefreshTick] = useState(0);
  const [selectedFeeStructureId, setSelectedFeeStructureId] = useState<string | null>(null);
  const [selectedFeeStructureClassId, setSelectedFeeStructureClassId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFeeStructuresLoading(true);
    setFeeStructuresError(null);

    apiClient
      .get('api/fee-structures?limit=100')
      .then((res: any) => {
        if (cancelled) return;
        if (res.data?.status === 'success') {
          const list = res.data.data || [];
          setFeeStructures(list);
          setSelectedFeeStructureId((prev) => prev ?? list[0]?.id ?? null);
        } else {
          setFeeStructures([]);
          setFeeStructuresError(res.data?.message || 'Failed to load fee structures.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setFeeStructures([]);
        setFeeStructuresError(err?.response?.data?.message || err?.message || 'An error occurred while fetching fee structures.');
      })
      .finally(() => {
        if (cancelled) return;
        setFeeStructuresLoading(false);
        setFeeStructuresFetched(true);
      });

    return () => { cancelled = true; };
  }, [open, feeStructuresRefreshTick]);

  // How many fee-structure rows each class has. Matches on class_id when the API
  // provides it, and falls back to class_name (as returned today) otherwise —
  // same "count per class from realClassOptions" pattern as the Subjects step.
  const classFeeStructureCounts = useMemo(() => {
    const map: Record<string, number> = {};
    realClassOptions.forEach((c) => { map[c.id] = 0; });
    feeStructures.forEach((fs: any) => {
      const matchedId =
        (fs.class_id && map[fs.class_id] !== undefined ? fs.class_id : null) ??
        realClassOptions.find((c) => c.label === fs.class_name)?.id ??
        null;
      if (matchedId && map[matchedId] !== undefined) map[matchedId] += 1;
    });
    return map;
  }, [feeStructures, realClassOptions]);

  const classesMissingFeeStructures = realClassOptions.filter((c) => (classFeeStructureCounts[c.id] ?? 0) === 0);

  useEffect(() => {
    if (!selectedFeeStructureClassId && realClassOptions.length > 0) {
      setSelectedFeeStructureClassId(realClassOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realClassOptions.length]);

  const viewedFeeStructures = useMemo(
    () =>
      feeStructures.filter((fs: any) => {
        if (!selectedFeeStructureClassId) return true;
        const cls = realClassOptions.find((c) => c.id === selectedFeeStructureClassId);
        return fs.class_id === selectedFeeStructureClassId || fs.class_name === cls?.label;
      }),
    [feeStructures, selectedFeeStructureClassId, realClassOptions]
  );

  // Fee Structures now waits on Classes (like Subjects does) since "complete"
  // means every class has at least one fee structure, not just "some rows exist".
  const feeStructuresChecking = classesChecking || feeStructuresLoading || !feeStructuresFetched;
  const feeStructuresComplete =
    !classesChecking &&
    feeStructuresFetched &&
    !feeStructuresError &&
    realClassOptions.length > 0 &&
    classesMissingFeeStructures.length === 0;

  const step6Status: StepStatus = useMemo(() => {
    if (feeStructuresChecking) return 'checking';
    return feeStructuresComplete ? 'completed' : 'current';
  }, [feeStructuresChecking, feeStructuresComplete]);

  // ── Step 7: Fee Late Config ──────────────────────────────────────
  // GET /fee-late-config → { status, message, data: (FeeLateConfig + class_name)[], pagination }
  const [lateConfigs, setLateConfigs] = useState<any[]>([]);
  const [lateConfigsLoading, setLateConfigsLoading] = useState(true);
  const [lateConfigsError, setLateConfigsError] = useState<string | null>(null);
  const [lateConfigsFetched, setLateConfigsFetched] = useState(false);
  const [lateConfigsRefreshTick, setLateConfigsRefreshTick] = useState(0);
  const [selectedLateConfigId, setSelectedLateConfigId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLateConfigsLoading(true);
    setLateConfigsError(null);

    apiClient
      .get('api/fee-late-config?limit=100')
      .then((res: any) => {
        if (cancelled) return;
        if (res.data?.status === 'success') {
          const list = res.data.data || [];
          setLateConfigs(list);
          setSelectedLateConfigId((prev) => prev ?? list[0]?.id ?? null);
        } else {
          setLateConfigs([]);
          setLateConfigsError(res.data?.message || 'Failed to load late fee configs.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLateConfigs([]);
        setLateConfigsError(err?.response?.data?.message || err?.message || 'An error occurred while fetching late fee configs.');
      })
      .finally(() => {
        if (cancelled) return;
        setLateConfigsLoading(false);
        setLateConfigsFetched(true);
      });

    return () => { cancelled = true; };
  }, [open, lateConfigsRefreshTick]);

  const lateConfigsChecking = lateConfigsLoading || !lateConfigsFetched;
  const lateConfigsComplete = lateConfigsFetched && !lateConfigsError && lateConfigs.length > 0;

  const step7Status: StepStatus = useMemo(() => {
    if (lateConfigsChecking) return 'checking';
    return lateConfigsComplete ? 'completed' : 'current';
  }, [lateConfigsChecking, lateConfigsComplete]);

  const selectedLateConfig = useMemo(
    () => lateConfigs.find((cfg: any) => cfg.id === selectedLateConfigId) || null,
    [lateConfigs, selectedLateConfigId]
  );

  const steps: { label: string; title: string; status: StepStatus; ctaLabel: string; route: string }[] = [
    { label: 'STEP 1', title: 'Classes', status: step1Status, ctaLabel: 'Set Up Classes', route: classesRoute },
    { label: 'STEP 2', title: 'Subjects', status: step2Status, ctaLabel: 'Set Up Subjects', route: subjectsRoute },
    { label: 'STEP 3', title: 'Timetable Setup', status: step3Status, ctaLabel: 'Set Up Timetable', route: timetableRoute },
    { label: 'STEP 4', title: 'Exam Types', status: step4Status, ctaLabel: 'Set Up Exam Types', route: examTypesRoute },
    { label: 'STEP 5', title: 'Fee Types', status: step5Status, ctaLabel: 'Set Up Fee Types', route: feeTypesRoute },
    { label: 'STEP 6', title: 'Fee Structures', status: step6Status, ctaLabel: 'Set Up Fee Structures', route: feeStructuresRoute },
    { label: 'STEP 7', title: 'Late Fee Config', status: step7Status, ctaLabel: 'Set Up Late Fee Config', route: feeLateConfigRoute },
  ];

  const completedCount = steps.filter((s) => s.status === 'completed').length;

  useEffect(() => {
    if (open) {
      const firstIncomplete = steps.findIndex((s) => s.status !== 'completed');
      setSelectedStepIndex(firstIncomplete === -1 ? 0 : firstIncomplete);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Close animation (matches BulkEditStudentsModal) ──────────────
  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.setup-stepper-modal .p-dialog') as HTMLElement | null;
      const mask = document.querySelector('.setup-stepper-modal') as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const goTo = (route: string) => {
    handleClose();
    navigate(route);
  };

  const activeStep = steps[selectedStepIndex];

  // ── Header ────────────────────────────────────────────────────────
  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div
        style={{
          width: 54, height: 54, borderRadius: 14,
          background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 22, flexShrink: 0,
          border: '3px solid white', boxShadow: '0 8px 24px rgba(59,130,246,.3)',
        }}
      >
        <i className="pi pi-verified" />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>System Setup Status</div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {completedCount} of {steps.length} checks complete
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '95vw', maxWidth: 960, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
          maxHeight: 'calc(92vh - 120px)',
          overflowY: 'auto',
        }}
        headerStyle={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '20px 24px', borderRadius: '24px 24px 0 0' }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="setup-stepper-modal"
      >
        <style>{`
          .setup-stepper-modal { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.25)!important; animation:setupMaskFadeIn .35s ease; }
          .setup-stepper-modal.mask-closing { animation:setupMaskFadeOut .4s ease!important; }
          @keyframes setupMaskFadeIn { from{opacity:0} to{opacity:1} }
          @keyframes setupMaskFadeOut { from{opacity:1} to{opacity:0} }
          .setup-stepper-modal .p-dialog { animation:setupDialogOpen .35s cubic-bezier(.4,0,.2,1); box-shadow:0 25px 50px -12px rgba(0,0,0,.25)!important; }
          .setup-stepper-modal .p-dialog.dialog-closing { animation:setupDialogClose .4s cubic-bezier(.4,0,.2,1)!important; }
          @keyframes setupDialogOpen { from{opacity:0;transform:scale(.92) translateY(-20px)} to{opacity:1;transform:scale(1) translateY(0)} }
          @keyframes setupDialogClose { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.88) translateY(25px)} }
          .setup-table .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:10px!important; border-bottom:1px solid #e5e7eb!important; }
          .setup-table .p-datatable-tbody > tr > td { padding:10px!important; border-bottom:1px solid #f3f4f6!important; }
          .setup-table .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; }
          .setup-table .p-datatable-tbody > tr.setup-row-selected { background:rgba(59,130,246,.08)!important; }
          .setup-type-chip { padding:6px 14px; border-radius:8px; font-size:12px; font-weight:600; cursor:pointer; border:1.5px solid #e5e7eb; background:white; color:#1f2937; transition: background 0.2s, border-color 0.2s, color 0.2s; }
          .setup-type-chip.active { border-color:#3b82f6; background:linear-gradient(135deg,#1e40af,#3b82f6); color:white; }
          .setup-fade { animation: setupFadeIn 0.35s ease; }
          @keyframes setupFadeIn { from{opacity:0; transform: translateY(4px);} to{opacity:1; transform: translateY(0);} }
        `}</style>

        {/* ── Stepper row ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', background: 'white', borderRadius: 16, border: '1px solid #e5e7eb', padding: '24px 28px', marginBottom: 18 }}>
          {steps.map((step, idx) => (
            <React.Fragment key={step.label}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 98 }}>
                <StepCircle status={step.status} selected={selectedStepIndex === idx} onClick={() => setSelectedStepIndex(idx)} />
                <div style={{ marginTop: 9, fontSize: 7.9, fontWeight: 700, letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase' }}>
                  {step.label}
                </div>
                <div style={{ marginTop: 3, fontSize: 10.9, fontWeight: 700, color: step.status === 'pending' ? '#9ca3af' : '#1f2937', textAlign: 'center' }}>
                  {step.title}
                </div>
                <div style={{ marginTop: 6, padding: '2.25px 9px', borderRadius: 20, fontSize: 8.25, fontWeight: 700, background: STATUS_META[step.status].pillBg, color: STATUS_META[step.status].pillColor }}>
                  {STATUS_META[step.status].label}
                </div>
              </div>

              {idx < steps.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 4,
                    marginTop: 20,
                    borderRadius: 4,
                    background:
                      step.status === 'completed' && steps[idx + 1].status === 'completed'
                        ? STATUS_META[step.status].circle
                        : `linear-gradient(to right, ${STATUS_META[step.status].circle}, ${STATUS_META[steps[idx + 1].status].circle})`,
                    boxShadow: step.status === 'completed' || step.status === 'current' ? '0 0 8px rgba(59,130,246,0.25)' : 'none',
                    transition: 'background 0.3s, box-shadow 0.3s',
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Detail panel for the selected step ── */}
        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e5e7eb', padding: 20, minHeight: 260 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1e40af' }}>{activeStep.title}</div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
                {selectedStepIndex === 0 && `${realClassOptions.length} class(es) found for the current session`}
                {selectedStepIndex === 1 &&
                  `${realClassOptions.length - classesMissingSubjects.length} of ${realClassOptions.length} class(es) have at least one subject`}
                {selectedStepIndex === 2 &&
                  `${realTypeOptions.length} timetable type(s), viewing slots for the selected type below`}
                {selectedStepIndex === 3 && `${examTypes.length} exam type(s) configured`}
                {selectedStepIndex === 4 && `${feeTypes.length} fee type(s) configured`}
                {selectedStepIndex === 5 &&
                  `${realClassOptions.length - classesMissingFeeStructures.length} of ${realClassOptions.length} class(es) have a fee structure · ${feeStructures.length} total row(s)`}
                {selectedStepIndex === 6 && `${lateConfigs.length} class(es) have a late fee config`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: STATUS_META[activeStep.status].pillBg, color: STATUS_META[activeStep.status].pillColor }}>
                {STATUS_META[activeStep.status].label}
              </span>
              {selectedStepIndex === 0 && (
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text p-button-rounded"
                  tooltip="Re-check classes"
                  tooltipOptions={{ position: 'top' }}
                  style={{ width: 30, height: 30, color: '#3b82f6' }}
                  onClick={refreshClasses}
                  loading={classesChecking}
                />
              )}
              {selectedStepIndex === 1 && (
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text p-button-rounded"
                  tooltip="Re-check subjects"
                  tooltipOptions={{ position: 'top' }}
                  style={{ width: 30, height: 30, color: '#3b82f6' }}
                  onClick={() => setSubjectsRefreshTick((t) => t + 1)}
                  loading={subjectsChecking}
                />
              )}
              {selectedStepIndex === 3 && (
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text p-button-rounded"
                  tooltip="Re-check exam types"
                  tooltipOptions={{ position: 'top' }}
                  style={{ width: 30, height: 30, color: '#3b82f6' }}
                  onClick={() => setExamTypesRefreshTick((t) => t + 1)}
                  loading={examTypesChecking}
                />
              )}
              {selectedStepIndex === 4 && (
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text p-button-rounded"
                  tooltip="Re-check fee types"
                  tooltipOptions={{ position: 'top' }}
                  style={{ width: 30, height: 30, color: '#3b82f6' }}
                  onClick={() => setFeeTypesRefreshTick((t) => t + 1)}
                  loading={feeTypesChecking}
                />
              )}
              {selectedStepIndex === 5 && (
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text p-button-rounded"
                  tooltip="Re-check fee structures"
                  tooltipOptions={{ position: 'top' }}
                  style={{ width: 30, height: 30, color: '#3b82f6' }}
                  onClick={() => setFeeStructuresRefreshTick((t) => t + 1)}
                  loading={feeStructuresChecking}
                />
              )}
              {selectedStepIndex === 6 && (
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text p-button-rounded"
                  tooltip="Re-check late fee configs"
                  tooltipOptions={{ position: 'top' }}
                  style={{ width: 30, height: 30, color: '#3b82f6' }}
                  onClick={() => setLateConfigsRefreshTick((t) => t + 1)}
                  loading={lateConfigsChecking}
                />
              )}
              {activeStep.status !== 'completed' && activeStep.status !== 'pending' && (
                <Button
                  label={activeStep.ctaLabel}
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  style={{ borderRadius: 8, fontSize: 12.5, background: '#3b82f6', border: 'none' }}
                  onClick={() => goTo(activeStep.route)}
                />
              )}
            </div>
          </div>

          {/* ── Step 1 content: classes API data ── */}
          {selectedStepIndex === 0 && (
            <div className="setup-table">
              {classesChecking ? (
                <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: 20, marginBottom: 8, display: 'block' }} />
                  Checking classes...
                </div>
              ) : realClassOptions.length === 0 ? (
                <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  <i className="pi pi-inbox" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                  No classes exist for the current session yet.
                </div>
              ) : (
                <DataTable value={realClassOptions} scrollable scrollHeight="220px">
                  <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                  <Column header="Class Name" field="label" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.label}</span>} />
                  <Column
                    header="Sections"
                    body={(r: any) =>
                      r.sections?.length ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {r.sections.map((s: string) => (
                            <span key={s} style={{ padding: '2px 8px', borderRadius: 12, background: '#eff6ff', color: '#1d4ed8', fontSize: 11, fontWeight: 600 }}>
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>No sections</span>
                      )
                    }
                  />
                  <Column header="Class ID" body={(r: any) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{r.id}</span>} />
                </DataTable>
              )}
            </div>
          )}

          {/* ── Step 2 content: subjects-by-class API data ── */}
          {selectedStepIndex === 1 && (
            <div>
              <>
                  <div className="setup-table" style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Subjects per Class</div>
                    {subjectsChecking ? (
                      <div key="subjects-checking" className="setup-fade" style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, marginBottom: 6, display: 'block' }} />
                        Checking subjects for each class...
                      </div>
                    ) : (
                      <DataTable key="subjects-table" className="setup-fade" value={realClassOptions}>
                        <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                        <Column header="Class Name" field="label" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.label}</span>} />
                        <Column
                          header="Subjects"
                          body={(r: any) => <span style={{ fontSize: 13, color: '#1f2937' }}>{subjectCounts[r.id] ?? 0}</span>}
                          style={{ width: 100 }}
                        />
                        <Column
                          header="Status"
                          body={(r: any) =>
                            (subjectCounts[r.id] ?? 0) > 0 ? (
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d' }}>
                                <i className="pi pi-check" style={{ fontSize: 9, marginRight: 4 }} />
                                Configured
                              </span>
                            ) : (
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#b91c1c' }}>
                                <i className="pi pi-times" style={{ fontSize: 9, marginRight: 4 }} />
                                Missing
                              </span>
                            )
                          }
                          style={{ width: 130 }}
                        />
                      </DataTable>
                    )}
                  </div>

                  {realClassOptions.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Subject List</div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        {realClassOptions.map((c) => (
                          <button
                            key={c.id}
                            className={`setup-type-chip ${selectedSubjectClassId === c.id ? 'active' : ''}`}
                            onClick={() => setSelectedSubjectClassId(c.id)}
                          >
                            {c.label}
                            {(subjectCounts[c.id] ?? 0) === 0 && !subjectsChecking && (
                              <span style={{ marginLeft: 6, color: '#ef4444' }}>●</span>
                            )}
                          </button>
                        ))}
                      </div>

                      <div>
                        {viewedSubjectsLoading ? (
                          <div key={`${selectedSubjectClassId}-loading`} className="setup-fade" style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, marginBottom: 6, display: 'block' }} />
                            Loading subjects...
                          </div>
                        ) : viewedSubjects.filter((s) => s.id !== '').length === 0 ? (
                          <div key={`${selectedSubjectClassId}-empty`} className="setup-fade" style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                            <i className="pi pi-book" style={{ fontSize: 22, marginBottom: 8, display: 'block' }} />
                            No subjects configured for this class.
                          </div>
                        ) : (
                          <div key={`${selectedSubjectClassId}-loaded`} className="setup-fade" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {viewedSubjects
                              .filter((s: any) => s.id !== '')
                              .map((s: any) => (
                                <span
                                  key={s.id}
                                  title={s.id}
                                  style={{
                                    padding: '6px 14px',
                                    borderRadius: 20,
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #dbeafe',
                                  }}
                                >
                                  {s.subjectName}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
            </div>
          )}

          {/* ── Step 3 content: timetable types + time slots API data ── */}
          {selectedStepIndex === 2 && (
            <div>
              <>
                  <div className="setup-table" style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Timetable Types</div>
                    {realTypeOptions.length === 0 ? (
                      <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        <i className="pi pi-inbox" style={{ fontSize: 22, marginBottom: 8, display: 'block' }} />
                        No timetable types configured for the current session.
                      </div>
                    ) : (
                      <DataTable value={realTypeOptions}>
                        <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                        <Column header="Type Name" field="label" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.label}</span>} />
                        <Column header="ID" body={(r: any) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{r.id}</span>} />
                      </DataTable>
                    )}
                  </div>

                  {realTypeOptions.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Time Slots</div>

                      {realTypeOptions.length > 1 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                          {realTypeOptions.map((t) => (
                            <button
                              key={t.id}
                              className={`setup-type-chip ${selectedTypeId === t.id ? 'active' : ''}`}
                              onClick={() => setSelectedTypeId(t.id)}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="setup-table">
                        {viewedSlotsLoading ? (
                          <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, marginBottom: 6, display: 'block' }} />
                            Loading time slots...
                          </div>
                        ) : viewedSlots.length === 0 ? (
                          <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                            <i className="pi pi-clock" style={{ fontSize: 22, marginBottom: 8, display: 'block' }} />
                            No time slots configured for this timetable type.
                          </div>
                        ) : (
                          <DataTable value={viewedSlots} scrollable scrollHeight="220px">
                            <Column header="#" field="periodNumber" style={{ width: 60 }} />
                            <Column header="Period" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.label}</span>} />
                            <Column header="Start" field="startTime" />
                            <Column header="End" field="endTime" />
                            <Column
                              header="Type"
                              body={(r: any) => (
                                <span
                                  style={{
                                    padding: '3px 10px',
                                    borderRadius: 8,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: 'capitalize',
                                    background: slotTypeColors[r.slotType]?.bg || '#f3f4f6',
                                    color: slotTypeColors[r.slotType]?.color || '#374151',
                                  }}
                                >
                                  {r.slotType}
                                </span>
                              )}
                            />
                          </DataTable>
                        )}
                      </div>
                    </div>
                  )}
                </>
            </div>
          )}

          {/* ── Step 4 content: exam types API data ── */}
          {selectedStepIndex === 3 && (
            <div>
              <div className="setup-table">
                  {examTypesChecking ? (
                    <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                      <i className="pi pi-spin pi-spinner" style={{ fontSize: 20, marginBottom: 8, display: 'block' }} />
                      Checking exam types...
                    </div>
                  ) : examTypesError ? (
                    <div style={{ padding: '30px 0', textAlign: 'center', color: '#b91c1c', fontSize: 13 }}>
                      <i className="pi pi-exclamation-circle" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                      Failed to load exam types. Please try again.
                    </div>
                  ) : examTypes.length === 0 ? (
                    <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                      <i className="pi pi-inbox" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                      No exam types exist for the current session yet.
                    </div>
                  ) : (
                    <DataTable
                      value={sortedExamTypes}
                      scrollable
                      scrollHeight="220px"
                      selectionMode="single"
                      onRowClick={(e: any) => setSelectedExamTypeId(e.data.id)}
                      rowClassName={(r: any) => (r.id === selectedExamTypeId ? 'setup-row-selected' : '')}
                    >
                      <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                      <Column header="Order" field="exam_order" style={{ width: 70 }} />
                      <Column header="Name" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.name}</span>} />
                      <Column header="Title" field="title" />
                      <Column
                        header="Status"
                        body={(r: any) => (
                          <span
                            style={{
                              padding: '3px 10px',
                              borderRadius: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: 'capitalize',
                              background: examStatusColors[r.status]?.bg || '#f3f4f6',
                              color: examStatusColors[r.status]?.color || '#374151',
                            }}
                          >
                            {r.status}
                          </span>
                        )}
                        style={{ width: 110 }}
                      />
                      <Column header="ID" body={(r: any) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{r.id}</span>} />
                    </DataTable>
                  )}
                </div>
            </div>
          )}

          {/* ── Step 5 content: fee types ── */}
          {selectedStepIndex === 4 && (
            <div className="setup-table">
              {feeTypesChecking ? (
                <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: 20, marginBottom: 8, display: 'block' }} />
                  Checking fee types...
                </div>
              ) : feeTypesError ? (
                <div style={{ padding: '30px 0', textAlign: 'center', color: '#b91c1c', fontSize: 13 }}>
                  <i className="pi pi-exclamation-circle" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                  Failed to load fee types. Please try again.
                </div>
              ) : feeTypes.length === 0 ? (
                <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  <i className="pi pi-inbox" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                  No fee types exist for the current school yet.
                </div>
              ) : (
                <DataTable
                  value={feeTypes}
                  scrollable
                  scrollHeight="220px"
                  selectionMode="single"
                  onRowClick={(e: any) => setSelectedFeeTypeId(e.data.id)}
                  rowClassName={(r: any) => (r.id === selectedFeeTypeId ? 'setup-row-selected' : '')}
                >
                  <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                  <Column header="Fee Name" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.fee_name}</span>} />
                  <Column
                    header="Status"
                    body={(r: any) => (
                      <span
                        style={{
                          padding: '3px 10px',
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'capitalize',
                          background: examStatusColors[r.status]?.bg || '#f3f4f6',
                          color: examStatusColors[r.status]?.color || '#374151',
                        }}
                      >
                        {r.status}
                      </span>
                    )}
                    style={{ width: 110 }}
                  />
                  <Column
                    header="Optional"
                    body={(r: any) => <span style={{ fontSize: 12.5, color: r.optional ? '#1d4ed8' : '#6b7280' }}>{r.optional ? 'Yes' : 'No'}</span>}
                    style={{ width: 90 }}
                  />
                  <Column header="ID" body={(r: any) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{r.id}</span>} />
                </DataTable>
              )}
            </div>
          )}

          {/* ── Step 6 content: fee structures, with per-class coverage ── */}
          {selectedStepIndex === 5 && (
            <div>
              <div className="setup-table" style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Fee Structures per Class</div>
                {feeStructuresChecking ? (
                  <div className="setup-fade" style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, marginBottom: 6, display: 'block' }} />
                    Checking fee structures for each class...
                  </div>
                ) : feeStructuresError ? (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: '#b91c1c', fontSize: 13 }}>
                    <i className="pi pi-exclamation-circle" style={{ fontSize: 22, marginBottom: 8, display: 'block' }} />
                    Failed to load fee structures. Please try again.
                  </div>
                ) : realClassOptions.length === 0 ? (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    <i className="pi pi-inbox" style={{ fontSize: 22, marginBottom: 8, display: 'block' }} />
                    No classes exist for the current session yet.
                  </div>
                ) : (
                  <DataTable className="setup-fade" value={realClassOptions}>
                    <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                    <Column header="Class Name" field="label" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.label}</span>} />
                    <Column
                      header="Fee Rows"
                      body={(r: any) => <span style={{ fontSize: 13, color: '#1f2937' }}>{classFeeStructureCounts[r.id] ?? 0}</span>}
                      style={{ width: 100 }}
                    />
                    <Column
                      header="Status"
                      body={(r: any) =>
                        (classFeeStructureCounts[r.id] ?? 0) > 0 ? (
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d' }}>
                            <i className="pi pi-check" style={{ fontSize: 9, marginRight: 4 }} />
                            Configured
                          </span>
                        ) : (
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#b91c1c' }}>
                            <i className="pi pi-times" style={{ fontSize: 9, marginRight: 4 }} />
                            Missing
                          </span>
                        )
                      }
                      style={{ width: 130 }}
                    />
                  </DataTable>
                )}
              </div>

              {realClassOptions.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Fee Structure List</div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    {realClassOptions.map((c) => (
                      <button
                        key={c.id}
                        className={`setup-type-chip ${selectedFeeStructureClassId === c.id ? 'active' : ''}`}
                        onClick={() => setSelectedFeeStructureClassId(c.id)}
                      >
                        {c.label}
                        {(classFeeStructureCounts[c.id] ?? 0) === 0 && !feeStructuresChecking && (
                          <span style={{ marginLeft: 6, color: '#ef4444' }}>●</span>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="setup-table">
                    {feeStructuresChecking ? (
                      <div key={`${selectedFeeStructureClassId}-loading`} className="setup-fade" style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, marginBottom: 6, display: 'block' }} />
                        Loading fee structures...
                      </div>
                    ) : viewedFeeStructures.length === 0 ? (
                      <div key={`${selectedFeeStructureClassId}-empty`} className="setup-fade" style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        <i className="pi pi-wallet" style={{ fontSize: 22, marginBottom: 8, display: 'block' }} />
                        No fee structures configured for this class.
                      </div>
                    ) : (
                      <DataTable
                        key={`${selectedFeeStructureClassId}-loaded`}
                        className="setup-fade"
                        value={viewedFeeStructures}
                        scrollable
                        scrollHeight="220px"
                        selectionMode="single"
                        onRowClick={(e: any) => setSelectedFeeStructureId(e.data.id)}
                        rowClassName={(r: any) => (r.id === selectedFeeStructureId ? 'setup-row-selected' : '')}
                      >
                        <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                        <Column header="Fee Type" field="fee_name" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.fee_name}</span>} />
                        <Column header="Period" body={(r: any) => <span style={{ fontSize: 13 }}>{formatMonthYear(r.month_year)} · {r.session}</span>} />
                        <Column header="Amount" body={(r: any) => <span style={{ fontWeight: 700, color: '#1f2937' }}>{formatCurrency(r.amount)}</span>} style={{ width: 110 }} />
                        <Column header="ID" body={(r: any) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{r.id}</span>} />
                      </DataTable>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 7 content: late fee config, with month drill-down ── */}
          {selectedStepIndex === 6 && (
            <div>
              <div className="setup-table" style={{ marginBottom: 18 }}>
                {lateConfigsChecking ? (
                  <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: 20, marginBottom: 8, display: 'block' }} />
                    Checking late fee configs...
                  </div>
                ) : lateConfigsError ? (
                  <div style={{ padding: '30px 0', textAlign: 'center', color: '#b91c1c', fontSize: 13 }}>
                    <i className="pi pi-exclamation-circle" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                    Failed to load late fee configs. Please try again.
                  </div>
                ) : lateConfigs.length === 0 ? (
                  <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    <i className="pi pi-inbox" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                    No late fee configs exist for the current school yet.
                  </div>
                ) : (
                  <DataTable
                    value={lateConfigs}
                    scrollable
                    scrollHeight="200px"
                    selectionMode="single"
                    onRowClick={(e: any) => setSelectedLateConfigId(e.data.id)}
                    rowClassName={(r: any) => (r.id === selectedLateConfigId ? 'setup-row-selected' : '')}
                  >
                    <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                    <Column header="Class" body={(r: any) => <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{r.class_name}</span>} />
                    <Column header="Session" field="session" style={{ width: 100 }} />
                    <Column header="Months Configured" body={(r: any) => <span>{r.months?.length ?? 0}</span>} style={{ width: 140 }} />
                    <Column header="ID" body={(r: any) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{r.id}</span>} />
                  </DataTable>
                )}
              </div>

              {selectedLateConfig && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                    Months for {selectedLateConfig.class_name}
                  </div>
                  <div className="setup-table">
                    {(selectedLateConfig.months?.length ?? 0) === 0 ? (
                      <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        <i className="pi pi-calendar-times" style={{ fontSize: 22, marginBottom: 8, display: 'block' }} />
                        No months configured for this class.
                      </div>
                    ) : (
                      <DataTable key={selectedLateConfig.id} className="setup-fade" value={selectedLateConfig.months} scrollable scrollHeight="220px">
                        <Column header="#" body={(_: any, o: any) => o.rowIndex + 1} style={{ width: 44 }} />
                        <Column header="Month" field="month_year" style={{ width: 90 }} />
                        <Column header="Due Date" field="due_date" style={{ width: 100 }} />
                        <Column
                          header="Late Fee Type"
                          body={(r: any) => (
                            <span
                              style={{
                                padding: '3px 10px',
                                borderRadius: 8,
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'capitalize',
                                background: lateFeeTypeColors[r.late_fee_type]?.bg || '#f3f4f6',
                                color: lateFeeTypeColors[r.late_fee_type]?.color || '#374151',
                              }}
                            >
                              {r.late_fee_type.replace('_', ' ')}
                            </span>
                          )}
                        />
                        <Column header="Amount" field="late_fee_amount" style={{ width: 90 }} />
                        <Column
                          header="Max"
                          body={(r: any) => <span>{r.late_fee_max ?? '—'}</span>}
                          style={{ width: 80 }}
                        />
                        <Column header="Grace Days" field="grace_days" style={{ width: 100 }} />
                      </DataTable>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer: step navigation + close ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16 }}>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            {completedCount === steps.length ? (
              <span style={{ color: '#059669', fontWeight: 600 }}>
                <i className="pi pi-check-circle" style={{ marginRight: 6 }} />
                Everything is set up
              </span>
            ) : (
              <span>
                <span style={{ fontWeight: 700, color: '#3b82f6' }}>{steps.length - completedCount}</span> check(s) still need attention
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              className="p-button-outlined"
              style={{ borderRadius: 10, fontSize: 13 }}
              disabled={selectedStepIndex === 0}
              onClick={() => setSelectedStepIndex((i) => Math.max(0, i - 1))}
            />
            <Button
              label="Next"
              icon="pi pi-arrow-right"
              iconPos="right"
              className="p-button-outlined"
              style={{ borderRadius: 10, fontSize: 13 }}
              disabled={selectedStepIndex === steps.length - 1}
              onClick={() => setSelectedStepIndex((i) => Math.min(steps.length - 1, i + 1))}
            />
            <Button
              label="Close"
              icon="pi pi-times"
              style={{ borderRadius: 10, fontSize: 13, background: '#1e40af', border: 'none' }}
              onClick={handleClose}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default SetupStepperModal;